// -----------------------------------------------------------------------
// <copyright company="Microsoft Corporation">
// Copyright (c) Microsoft Corporation. All rights reserved.
// </copyright>
// -----------------------------------------------------------------------

import { Observable } from './observable';
import type { IObservableProperty, ISubscriptionOptions } from './observable.types';

/**
 * Specific implementation of a property that wraps a value and will emit notifications when the value is
 * updated or when first subscribing.
 *
 * @remarks
 * Expose to external APIs as IReadOnlyObservableProperty<T> if external APIs should only be able to read the
 * value; or as IObservableProperty<T> if external APIs should also be able to set the value.
 */
export class ObservableProperty<T> implements IObservableProperty<T> {
  protected _observable: Observable<T>;

  protected _value: T;

  private readonly onCallbackError: ((e: unknown) => void) | undefined;

  /**
   * Creates an instance of ObservableProperty.
   *
   * @param value - The initial value for the property.
   * @param onCallbackError - Function that will be executed if any callback errors when notifying.
   */
  public constructor(value: T, onCallbackError?: (e: unknown) => void) {
    this._observable = new Observable(onCallbackError);
    this._value = value;
    this.onCallbackError = onCallbackError;
  }

  /**
   * Removes all of the observable's subscriptions at once by clearing the internal list of observers.
   */
  public unsubscribeAll(): void {
    this._observable.unsubscribeAll();
  }

  public get value(): T {
    return this._value;
  }

  public set value(newValue: T) {
    if (newValue !== this._value) {
      this._value = newValue;
      this._observable.notify(newValue);
    }
  }

  /**
   * Subscribe to the inner observable to be notified when the value changes. Also sends out the initial value
   * so subscribers don't have to fuss over checking the value property.
   *
   * @param callback - Callback to recieve initial value and any value changes.
   * @param options - Options availible when doing an Observable.prototype.subscribe.
   *
   * @returns Function to remove this subscription.
   */
  public subscribe(callback: (newValue: T) => void, options?: ISubscriptionOptions): () => void {
    const notifyCurrentValue = !options?.ignoreCurrentValue;
    if (notifyCurrentValue) {
      try {
        callback(this._value);
      } catch (e) {
        if (this.onCallbackError) {
          this.onCallbackError(e);
        }
      }
    }
    if (options?.unregisterOnFirstCall && notifyCurrentValue) {
      return () => {
        // no-op
      };
    } else {
      // unregisterOnFirstCall is handled by Observable internally
      return this._observable.subscribe(callback, options);
    }
  }
}
